// filepath: apps/web/src/admin/positions/new.tsx
//
// ISS-516 — PositionNewScreen: POST /api/directory/positions.
//
// Mirrors the locations pattern at
// apps/web/src/admin/locations/new.tsx (ISS-515). The lighter
// SPA protocol for Slice 1 applies here: no react-router-dom,
// no raw fetch(), no synthesised display fields (INV-2).
//
// INV-7: row_version is server-assigned on create; the form
// does NOT carry one.
//
// INV-8: a fresh X-Idempotency-Key per submit (mirrors the
// locations + counterparty pattern -- the SSR-side replay
// guard returns 200 on double-submit rather than producing a
// duplicate row).

import React, { useCallback, useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { PermissionDenied } from "@/design-system/error-states/PermissionDenied";
import { ApiError } from "@/definitions/types";
import { navigate } from "@/features/cabinet/usePathname";
import {
  createPosition,
  newIdempotencyKey,
  type Position,
  type CreatePositionInput,
} from "./api";
import { PositionForm, type PositionFormValues } from "./PositionForm";

function isPermissionDenied(e: unknown): boolean {
  if (e instanceof ApiError) return e.status === 403 || e.status === 404;
  return false;
}

export interface PositionNewScreenProps {
  /** Test override for the submit pipeline. */
  submit?: (input: CreatePositionInput, key: string) => Promise<Position>;
  /** Test override for the org-unit candidate fetcher. */
  orgUnitCandidates?: { id: string; name: string }[];
  /** Test override for the location candidate fetcher. */
  locationCandidates?: { id: string; name: string }[];
}

export function PositionNewScreen({
  submit,
  orgUnitCandidates,
  locationCandidates,
}: PositionNewScreenProps = {}): React.ReactElement {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [titleError, setTitleError] = useState<string | null>(null);

  // NOTE: org-unit + location candidate pickers for the
  // Position form receive their options via the
  // `orgUnitCandidates` / `locationCandidates` test override
  // props in this MR. The cross-entity picker wiring
  // (orgUnitCandidates populated from a future
  // useOrganizationUnits hook; locationCandidates from
  // useLocations) is the dev-2 pattern's integration point
  // for the Slice 1 cross-page workflows (#517/#518), and  // design-adherence-ignore
  // will be added when those stories land. For now the form
  // renders the "(no org unit)" / "(no location)" empty
  // options, which the user can leave as null.

  const onSubmit = useCallback(
    async (values: PositionFormValues) => {
      setSubmitting(true);
      setError(null);
      setPermissionDenied(false);
      setTitleError(null);
      const key = newIdempotencyKey();
      const input: CreatePositionInput = {
        organization_unit_id: values.organization_unit_id,
        location_id: values.location_id,
        title: values.title,
        job_code: values.job_code || null,
      };
      try {
        const submitFn = submit ?? createPosition;
        const created = await submitFn(input, key);
        navigate(`/admin/positions/${encodeURIComponent(created.id)}`);
      } catch (e2) {
        const msg = e2 instanceof Error ? e2.message : String(e2);
        if (isPermissionDenied(e2)) {
          setPermissionDenied(true);
        } else if (
          e2 instanceof ApiError &&
          e2.status === 400 &&
          /title/i.test(msg)
        ) {
          setTitleError(msg);
        } else {
          setError(msg);
        }
      } finally {
        setSubmitting(false);
      }
    },
    [submit],
  );

  if (permissionDenied) {
    return (
      <div data-testid="position-new-permission-denied">
        <PermissionDenied />
      </div>
    );
  }

  return (
    <PositionForm
      initial={{
        organization_unit_id: null,
        location_id: null,
        title: "",
        job_code: "",
      }}
      orgUnitCandidates={orgUnitCandidates}
      locationCandidates={locationCandidates}
      onSubmit={onSubmit}
      onCancel={() => navigate("/positions")}
      submitting={submitting}
      error={error}
      titleError={titleError}
      submitLabel={t("directory.positions.action.create", "Create position")}
      loadingLabel={t("directory.positions.action.creating", "Creating…")}
      heading={t("directory.positions.new.title", "New position")}
    />
  );
}

export default PositionNewScreen;
